import { createContext, useContext } from "react";

/**
 * Provenance for a single backend-resolved data key — where the frozen/live value
 * was sourced from, for the chip's provenance tag and source-document link.
 */
export interface DynamicContentProvenance {
  sourceDocumentId: string;
  sourceDocumentTitle: string;
  sourceDeviceId: string | null;
}

/** A single resolved data key as returned by the backend dynamic-content endpoint. */
export interface DynamicContentDataKey {
  /**
   * The resolved value. For a file answer this is `![filename](/share/token)` /
   * `[filename](url)` markdown, serialized server-side by the same `formatAnswer`
   * the snapshot bake uses, so the node and the snapshot always agree.
   */
  value: string | null;
  provenance: DynamicContentProvenance | null;
  isUnresolved: boolean;
}

/**
 * Payload of `GET /organization/:orgId/document/:docId/dynamic-content`: the
 * backend-resolved data keys (two-tier resolution + freezing already applied) plus
 * the always-live non-answer context map (device/org characteristics, metadata).
 */
export interface DynamicContentResolution {
  dataKeys: Record<string, DynamicContentDataKey>;
  context: Record<string, unknown>;
}

/** Result of resolving a single data key against the current answer context. */
export interface DataKeyResolution {
  /** The resolved value, or null when nothing resolves (renders empty). */
  value: string | null;
  /** Title of the document the value came from, for the provenance tag. */
  sourceDocumentTitle: string | null;
  /** Id of the source document, for building a link to it (null for context keys). */
  sourceDocumentId: string | null;
  /** Source document's device id, needed to build the QMS doc route. */
  sourceDocumentDeviceId: string | null;
  /** True when no value could be resolved (own answers, then revisions, then null). */
  isUnresolved: boolean;
}

/**
 * Resolution surface consumed by the dynamic node views. Provided by the editor
 * (see `DynamicNodeProvider` wiring in TiptapEditor) so the nodes stay stateless
 * and resolve live against whichever answer set the viewing context dictates
 * (draft -> latest two-tier; approved -> that version's connected answers).
 */
/** A selectable option (data key or context variable) shown in a picker bubble. */
export interface DynamicNodeOwner {
  /** Title shown in the chain control's accessible label and tooltip. */
  title: string;
  /** Existing owner document. Null means its template exists before creation. */
  documentId: string | null;
  /** Device scope for document routing. Null means organization scope. */
  deviceId: string | null;
  /** Version to open when following a document-reference link. */
  versionId?: string;
}

export interface DynamicNodeOption {
  /** The id stored on the node and resolved at render time. */
  key: string;
  /** Human-friendly label shown in the menu, when available. */
  label?: string;
  /** Supporting text shown beneath the label, when available. */
  subtitle?: string;
  /** Document that owns the key, or the template-defined expected owner. */
  owner?: DynamicNodeOwner;
}

export interface DynamicNodeContextValue {
  /** Resolve a bare data key to a value + provenance (answer tiers only). */
  resolveDataKey: (dataKey: string) => DataKeyResolution;
  /** Resolve a context variable (device/org/metadata) to a value. */
  resolveContext: (contextKey: string) => DataKeyResolution;
  /** Available context variables for the context node's picker. */
  contextKeys: DynamicNodeOption[];
  /** Find link/status metadata for a stored document-reference key and value. */
  resolveDocumentReferenceOwner?: (
    key: string,
    value: string
  ) => DynamicNodeOwner | undefined;
  /** Evaluate a raw handlebars template fragment to rendered markdown text. */
  evaluateHandlebars: (template: string) => string;
  /** Whether inactive conditional content should remain visible in the editor. */
  showAllConditionals: boolean;
  /** Set whether inactive conditional content remains visible. */
  onShowAllConditionalsChange: (showAll: boolean) => void;
  /** Whether we're viewing a frozen/approved version (affects styling only). */
  frozen: boolean;
  /** Whether the current draft was just restored and may show updated values. */
  restoredWithCurrentData: boolean;
  /** True while the answer/context data backing resolution is still loading. */
  loading: boolean;
  /**
   * Report the set of data keys a node needs (keyed by a stable node id). The
   * provider unions all registrations into the exact set requested from the
   * backend. Stable across renders — safe to call from an effect.
   */
  registerKeys: (id: string, keys: string[]) => void;
  /** Drop a node's registration (call on unmount). Stable across renders. */
  unregisterKeys: (id: string) => void;
  /**
   * True for a key we've asked for but don't yet have a value for while a fetch
   * is in flight — drives the per-node spinner without flickering resolved chips.
   */
  isKeyPending: (key: string) => boolean;
}

const NULL_RESOLUTION: DataKeyResolution = {
  value: null,
  sourceDocumentTitle: null,
  sourceDocumentId: null,
  sourceDocumentDeviceId: null,
  isUnresolved: true,
};

/**
 * Default no-op context: when the editor is rendered without a provider (e.g. a
 * standalone preview), data keys fall back to showing their key id and handlebars
 * fragments show their raw template, rather than throwing.
 */
const DEFAULT_CONTEXT: DynamicNodeContextValue = {
  resolveDataKey: () => NULL_RESOLUTION,
  resolveContext: () => NULL_RESOLUTION,
  contextKeys: [],
  evaluateHandlebars: (template) => template,
  showAllConditionals: false,
  onShowAllConditionalsChange: () => {},
  frozen: false,
  restoredWithCurrentData: false,
  loading: false,
  registerKeys: () => {},
  unregisterKeys: () => {},
  isKeyPending: () => false,
};

const DynamicNodeContext =
  createContext<DynamicNodeContextValue>(DEFAULT_CONTEXT);

export const DynamicNodeProvider = DynamicNodeContext.Provider;

export const useDynamicNodeContext = (): DynamicNodeContextValue =>
  useContext(DynamicNodeContext);
